import React from 'react';
import { Box, Button, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'motion/react';
import { Step, useStep } from '../../../state/map';
import ModalHeader from './ModalHeader';
import { LocationStep } from '../steps/location';
import { PinsStep } from '../steps/pins';
import { StyleStep } from '../steps/style';
import { LayoutStep } from '../steps/layout';
import { BorderStep } from '../steps/border';
import { SizeStep } from '../steps/size';

interface FullScreenModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const stepOrder = [Step.Location, Step.Pins, Step.Style, Step.Layout, Step.Border, Step.Size];

const getStepNumber = (step: Step): number => {
  return stepOrder.indexOf(step) + 1;
};

const FullScreenModal: React.FC<FullScreenModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const theme = useTheme();
  const currentStep = useStep();

  const renderStepContent = () => {
    switch (currentStep) {
      case Step.Location:
        return <LocationStep expanded={true} />;
      case Step.Pins:
        return <PinsStep expanded={true} />;
      case Step.Style:
        return <StyleStep expanded={true} />;
      case Step.Layout:
        return <LayoutStep expanded={true} />;
      case Step.Border:
        return <BorderStep expanded={true} />;
      case Step.Size:
        return <SizeStep expanded={true} />;
      default:
        return <LocationStep expanded={true} />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Box
          component={motion.div}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ 
            type: "spring", 
            stiffness: 300, 
            damping: 30,
            mass: 0.8
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme.palette.background.default,
            zIndex: (theme) => theme.zIndex.modal + 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <ModalHeader
            step={currentStep}
            stepNumber={getStepNumber(currentStep)}
            totalSteps={stepOrder.length}
            onBack={onClose}
          />

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              padding: '0',
              backgroundColor: theme.palette.background.default
            }}
          >
            {renderStepContent()}
          </Box>

          {/* Save Button */}
          <Box
            sx={{
              padding: '20px',
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Button
              onClick={onClose}
              variant="contained"
              fullWidth
              sx={{
                padding: '16px',
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 600,
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '&:active': {
                  transform: 'scale(0.98)',
                }
              }}
            >
              Save & Return to Map
            </Button>
          </Box>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default FullScreenModal;
